import React, { useState, useRef } from 'react';
import { admin } from '../../services/api';
import '../../styles/AdminQueueModals.css';

function BulkImportModal({ show, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const sampleCSV = `setid,medication_name,category,drug_class,priority
abc-123,Keytruda,Specialty,Oncology,HIGH
xyz-456,Metformin,Generic,Biguanide,MEDIUM
def-789,Ibrance,Specialty,CDK4/6 Inhibitor,HIGH`;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setFile(file);
    setResults(null);

    // Read and preview first 5 rows
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const previewLines = lines.slice(0, 6); // Header + 5 rows
      
      const parsed = previewLines.map(line => {
        // Simple CSV parsing (doesn't handle quotes properly but ok for preview)
        return line.split(',').map(cell => cell.trim());
      });
      
      setPreview(parsed);
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await admin.bulkImportQueue(formData);
      setResults(response.data);
      
      // Show summary alert
      const { added_count, skipped_count, error_count } = response.data;
      alert(
        `Import Complete!\n\n` +
        `✅ Added: ${added_count}\n` +
        `⏭️ Skipped: ${skipped_count} (duplicates)\n` +
        `❌ Errors: ${error_count}`
      );
      
      if (added_count > 0) {
        onSuccess && onSuccess();
      }
    } catch (err) {
      console.error('Bulk import failed:', err);
      alert('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'queue-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setPreview([]);
      setResults(null);
      setDragActive(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content queue-modal bulk-import-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Bulk Import from CSV</h3>
        
        <div className="bulk-import-content">
          {!results ? (
            <>
              <div className="template-download">
                <button 
                  type="button" 
                  className="btn-link"
                  onClick={downloadTemplate}
                >
                  📥 Download CSV Template
                </button>
              </div>

              <div
                className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
                <div className="drop-zone-content">
                  <div className="drop-zone-icon">📄</div>
                  <div className="drop-zone-text">
                    {file ? (
                      <>
                        <strong>{file.name}</strong>
                        <br />
                        <small>Click to change file</small>
                      </>
                    ) : (
                      <>
                        <strong>Drop CSV file here or click to browse</strong>
                        <br />
                        <small>Format: setid, medication_name, category, drug_class, priority</small>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {preview.length > 0 && (
                <div className="csv-preview">
                  <h4>Preview (first 5 rows)</h4>
                  <div className="preview-table-container">
                    <table className="preview-table">
                      <thead>
                        <tr>
                          {preview[0].map((header, idx) => (
                            <th key={idx}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.slice(1).map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="import-results">
              <h4>Import Results</h4>
              
              <div className="results-summary">
                <div className="result-stat result-added">
                  <span className="result-icon">✅</span>
                  <span className="result-label">Added:</span>
                  <span className="result-value">{results.added_count}</span>
                </div>
                
                <div className="result-stat result-skipped">
                  <span className="result-icon">⏭️</span>
                  <span className="result-label">Skipped:</span>
                  <span className="result-value">{results.skipped_count}</span>
                </div>
                
                <div className="result-stat result-errors">
                  <span className="result-icon">❌</span>
                  <span className="result-label">Errors:</span>
                  <span className="result-value">{results.error_count}</span>
                </div>
              </div>

              {results.skipped && results.skipped.length > 0 && (
                <div className="results-detail">
                  <h5>Skipped (Duplicates)</h5>
                  <ul>
                    {results.skipped.slice(0, 10).map((item, idx) => (
                      <li key={idx}>
                        {item.setid}: {item.reason}
                      </li>
                    ))}
                    {results.skipped.length > 10 && (
                      <li>... and {results.skipped.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              {results.errors && results.errors.length > 0 && (
                <div className="results-detail">
                  <h5>Errors</h5>
                  <ul>
                    {results.errors.slice(0, 10).map((error, idx) => (
                      <li key={idx} className="error-item">{error}</li>
                    ))}
                    {results.errors.length > 10 && (
                      <li>... and {results.errors.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="modal-actions">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={handleClose}
            disabled={uploading}
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button 
              type="button" 
              className="btn-primary"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? 'Uploading...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkImportModal;
